import { useCallback, useEffect, useReducer } from 'react'

import features from 'common/college-features'
import useCollegeFeatures from 'common/use-college-features'
import { useGuaranteedScid } from 'common/use-scid'
import useSnackbar from 'common/use-snackbar'
import { useIntegrationsAndDeliveryOptions } from 'store/college/hooks'

import IntegrationConfigurationContext from './Context'
import {
  CreateSFTPConfigurationParams,
  UpdateCampusESPConfiguration,
  createSFTPConfiguration,
  forceNewEasySlateIndexFields,
  generateSftpKeyPair,
  getSFTPConfigurations,
  regenerateSFTPKey,
  removeSftpKeyPair,
  saveServerCredentials,
  saveServerLocation,
  saveSlateDomain,
  testConnection,
  testSFTPConfiguration,
  updateBatchTranscriptSetting,
  updateConfiguredDeliveryMethod,
  updateDocsIntegrationStatus,
  updateHandraiserToggle,
  updateSFTPConfiguration,
  updateSendParentHandraisersBeforeStudentCheckbox,
} from './integration-service'
import integrationsReducer, { initialState } from './integrationsReducer'

const IntegrationConfigurationProvider = ({ children }: { children: React.ReactNode }) => {
  const scid = useGuaranteedScid()

  const [state, dispatch] = useReducer(integrationsReducer, initialState)
  const integrations = useIntegrationsAndDeliveryOptions()
  const setSnackbarError = useSnackbar('An error occurred while attempting to load or modify the SFTP configuration.')
  const isCampusESPEnabled = useCollegeFeatures(features.SUPPORTS_CAMPUS_ESP)

  const fetchIntegrations = useCallback(async () => {
    if (isCampusESPEnabled) {
      try {
        const res = await getSFTPConfigurations()
        dispatch({
          type: 'load-campus-esp-configuration',
          campusESPConfiguration: res.configurations.find((config) => config.type === 'campusESP'),
        })
      } catch {
        setSnackbarError()
      }
    }
    dispatch({
      type: 'load-integrations',
      integrations: integrations.integrations,
      fileDeliveryOptions: integrations.fileDeliveryOptions,
    })
  }, [integrations.integrations, integrations.fileDeliveryOptions, isCampusESPEnabled, setSnackbarError])

  const onSaveSlateDomain = useCallback(
    async ({ domain }: { domain: string }) => {
      // Skip if no changes
      if (domain === state.Slate?.Subdomain) {
        return true
      }
      try {
        const res = await saveSlateDomain({ scid, domain })
        dispatch({ type: 'update-integrations', integrations: res })
        return true
      } catch {
        setSnackbarError()
        return false
      }
    },
    [scid, setSnackbarError, state.Slate?.Subdomain],
  )

  const onTestSlateConnection = useCallback(
    async ({ domain }) => {
      // First update domain if needed - don't test against a domain that failed to save
      const isDomainSaved = await onSaveSlateDomain({ domain })
      if (!isDomainSaved) {
        return
      }
      // Reset Error State
      dispatch({ type: 'set-slate-test-connection', testSuccessful: undefined })
      // Test Slate connection
      try {
        await testConnection({ scid, type: 'slate' })
        dispatch({ type: 'set-slate-test-connection', testSuccessful: true })
      } catch {
        dispatch({ type: 'set-slate-test-connection', testSuccessful: false })
      }
    },
    [onSaveSlateDomain, scid],
  )

  const onActivateDocsToggle = useCallback(
    async ({ checked, type }: { checked: boolean; type: 'slate' | 'sftp' }) => {
      const finalType = type || state.ConfiguredDeliveryMethod
      // if documents are batched - update to one-to-one before activating docs integration to slate
      if (state.ConcatenateBatchTranscripts && finalType === 'slate' && checked) {
        await updateBatchTranscriptSetting({ scid, isBatched: false })
      }
      try {
        const res = await updateDocsIntegrationStatus({ scid, activate: checked, type: finalType })
        dispatch({ type: 'update-integrations', integrations: res })
      } catch {
        setSnackbarError()
      }
    },
    [scid, setSnackbarError, state.ConcatenateBatchTranscripts, state.ConfiguredDeliveryMethod],
  )

  const onActivateHandraisersToggle = useCallback(
    async ({ checked }: { checked: boolean }) => {
      try {
        const res = await updateHandraiserToggle({ scid, enabled: checked })
        dispatch({ type: 'update-file-delivery-options', fileDeliveryOptions: res })
      } catch {
        setSnackbarError()
      }
    },
    [scid, setSnackbarError],
  )

  const onSendParentHandraisersBeforeStudent = useCallback(
    async ({ checked }: { checked: boolean }) => {
      try {
        const res = await updateSendParentHandraisersBeforeStudentCheckbox({
          scid,
          sendParentHandraisersBeforeStudent: checked,
        })
        dispatch({ type: 'update-file-delivery-options', fileDeliveryOptions: res })
      } catch {
        setSnackbarError()
      }
    },
    [scid, setSnackbarError],
  )

  const onUpdateBatchTranscriptSetting = useCallback(
    async ({ isBatched }: { isBatched: boolean }) => {
      await updateBatchTranscriptSetting({ scid, isBatched })
      dispatch({ type: 'update-batch-transcript-setting', concatenateBatchTranscripts: isBatched })
    },
    [scid],
  )

  const onTestSftpConnection = useCallback(async () => {
    // Reset Error State
    dispatch({ type: 'set-sftp-test-connection', testSuccessful: undefined })
    try {
      await testConnection({ scid, type: 'sftp' })
      dispatch({ type: 'set-sftp-test-connection', testSuccessful: true })
    } catch {
      dispatch({ type: 'set-sftp-test-connection', testSuccessful: false })
    }
  }, [scid])

  const onSaveServerLocation = useCallback(
    async ({ hostname, port, path }) => {
      // Skip if no changes
      if (
        hostname === state.SFTP?.Credentials?.Host &&
        port === state.SFTP?.Credentials?.Port &&
        path === state.SFTP?.Credentials?.Path
      ) {
        return
      }
      try {
        const res = await saveServerLocation({ scid, hostname, port, path })
        dispatch({ type: 'update-integrations', integrations: res })
      } catch {
        setSnackbarError()
      }
    },
    [scid, state.SFTP?.Credentials, setSnackbarError],
  )

  const onSaveServerCredentials = useCallback(
    async ({ username, password }: { username: string; password?: string }) => {
      // Skip if no changes
      if (username === state.SFTP?.Credentials?.Username && !password) {
        return
      }
      try {
        const res = await saveServerCredentials({ scid, username, password })
        dispatch({ type: 'update-integrations', integrations: res })
      } catch {
        setSnackbarError()
      }
    },
    [scid, state.SFTP?.Credentials, setSnackbarError],
  )

  const onKeyGeneration = useCallback(async () => {
    try {
      const res = await generateSftpKeyPair({ scid })
      dispatch({ type: 'update-integrations', integrations: res, keySuccessful: true })
    } catch {
      setSnackbarError()
    }
  }, [scid, setSnackbarError])

  const onKeyRemoval = useCallback(async () => {
    try {
      const res = await removeSftpKeyPair({ scid })
      dispatch({ type: 'update-integrations', integrations: res })
    } catch {
      setSnackbarError()
    }
  }, [scid, setSnackbarError])

  // Handle Configuration Selection and Update
  const onConfigurationChange = useCallback(
    async ({ type }: { type: 'slate' | 'sftp' }) => {
      // If docs are not currently live - just update configuration method
      if (!state.isDocsDeliveryEnabled) {
        await updateConfiguredDeliveryMethod({ scid, deliveryMethod: type })
      } else {
        // Switch docs setting to new configuration and update local state
        await onActivateDocsToggle({ checked: true, type })
        await updateConfiguredDeliveryMethod({ scid, deliveryMethod: type })
      }
      dispatch({ type: 'update-configured-delivery-method', deliveryMethod: type })
    },
    [onActivateDocsToggle, scid, state.isDocsDeliveryEnabled],
  )

  // To be deprecated - As of 02/2026 - 16 schools left
  const onForceNewEasySlateIndexFields = useCallback(async () => {
    await forceNewEasySlateIndexFields({ scid })
    dispatch({ type: 'update-force-new-easy-slate-index-fields', forceNewEasySlateIndexFields: true })
  }, [scid])

  // When type = "campusESP" API auto generates a key and returns it in the response.
  const onCreateSFTPConfiguration = useCallback(
    async ({ type = 'campusESP', username }: CreateSFTPConfigurationParams) => {
      try {
        const res = await createSFTPConfiguration({ type, username })
        dispatch({ type: 'set-campus-esp-configuration', campusESPConfiguration: res })
      } catch {
        setSnackbarError()
      }
    },
    [setSnackbarError],
  )

  const onUpdateSFTPConfiguration = useCallback(
    async ({ id, username, enabled }: UpdateCampusESPConfiguration) => {
      try {
        const res = await updateSFTPConfiguration({ id, username, enabled })
        dispatch({ type: 'set-campus-esp-configuration', campusESPConfiguration: res })
      } catch {
        setSnackbarError()
      }
    },
    [setSnackbarError],
  )

  const onRegenerateSFTPKey = useCallback(
    async ({ id }: { id: string }) => {
      try {
        const res = await regenerateSFTPKey({ id })
        dispatch({ type: 'set-campus-esp-configuration', campusESPConfiguration: res })
      } catch {
        setSnackbarError()
      }
    },
    [setSnackbarError],
  )

  const onTestSFTPConfiguration = useCallback(
    async ({ id, username }: { id: string; username?: string }) => {
      dispatch({ type: 'test-sftp-configuration', testSuccessful: undefined })
      // Save username first if it changed
      if (username !== undefined && username !== state.campusESPConfiguration?.username) {
        try {
          // Always disable the connection if the username has changed
          const res = await updateSFTPConfiguration({ id, username, enabled: false })
          dispatch({ type: 'set-campus-esp-configuration', campusESPConfiguration: res })
        } catch {
          setSnackbarError()
          return
        }
      }
      try {
        await testSFTPConfiguration({ id })
        dispatch({ type: 'test-sftp-configuration', testSuccessful: true })
      } catch {
        dispatch({ type: 'test-sftp-configuration', testSuccessful: false })
      }
    },
    [setSnackbarError, state.campusESPConfiguration?.username],
  )

  useEffect(() => {
    // Fetch Integrations on Load
    if (state.isLoading) {
      fetchIntegrations()
    }
  }, [fetchIntegrations, state.isLoading])

  return (
    <IntegrationConfigurationContext.Provider
      value={{
        fetchIntegrations,
        onActivateDocsToggle,
        onActivateHandraisersToggle,
        onSendParentHandraisersBeforeStudent,
        onConfigurationChange,
        onForceNewEasySlateIndexFields,
        onKeyGeneration,
        onKeyRemoval,
        onSaveServerCredentials,
        onSaveServerLocation,
        onSaveSlateDomain,
        onTestSftpConnection,
        onTestSFTPConfiguration,
        onTestSlateConnection,
        onUpdateBatchTranscriptSetting,
        onCreateSFTPConfiguration,
        onUpdateSFTPConfiguration,
        onRegenerateSFTPKey,
        scid,
        sftpTestSuccessful: state.sftpTestSuccessful,
        slateTestSuccessful: state.slateTestSuccessful,
        campusESPTestSuccessful: state.campusESPTestSuccessful,
        state,
      }}
    >
      {children}
    </IntegrationConfigurationContext.Provider>
  )
}

export default IntegrationConfigurationProvider
